'use client';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { useToast } from '@/shared/components/ui/use-toast';
import { storyKeys } from '../queryKeys';
import { toggleFavorite } from '../actions/toggleFavorite';
import type { Story } from '../types/story';
import type { GetStoriesResult } from '../actions/getStories';

/**
 * Mutation hook that toggles the favorite flag on a story.
 *
 * Applies optimistic updates to:
 *   - all matching storyKeys.list(*) cache entries
 *   - the storyKeys.detail(storyId) cache entry
 *
 * Rolls back on error and shows a toast.
 */
export function useToggleStoryFavorite(familyId?: string | null) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const t = useTranslations('stories.favorites');

  return useMutation({
    mutationFn: (storyId: string) => toggleFavorite(storyId),

    onMutate: async (storyId: string) => {
      // Cancel in-flight refetches to avoid race conditions
      await queryClient.cancelQueries({ queryKey: storyKeys.lists() });
      await queryClient.cancelQueries({ queryKey: storyKeys.detail(storyId) });

      // ── Snapshot existing data for rollback ──────────────────────────

      // Detail cache
      const previousDetail = queryClient.getQueryData<Story>(storyKeys.detail(storyId));

      // All list caches (any familyId / filter combination)
      const listCacheEntries: Array<{ queryKey: readonly unknown[]; data: GetStoriesResult }> = [];
      queryClient.getQueriesData<GetStoriesResult>({ queryKey: storyKeys.lists() }).forEach(
        ([queryKey, data]) => {
          if (data) listCacheEntries.push({ queryKey, data });
        }
      );

      // ── Optimistic flip ──────────────────────────────────────────────

      const flipStory = (s: Story): Story =>
        s.id === storyId
          ? { ...s, isFavorite: !s.isFavorite, favoritedAt: !s.isFavorite ? new Date().toISOString() : null }
          : s;

      // Update detail
      if (previousDetail) {
        queryClient.setQueryData<Story>(storyKeys.detail(storyId), flipStory(previousDetail));
      }

      // Update every list cache entry
      for (const { queryKey, data } of listCacheEntries) {
        queryClient.setQueryData<GetStoriesResult>(queryKey, {
          ...data,
          stories: data.stories.map(flipStory),
        });
      }

      return { previousDetail, listCacheEntries };
    },

    onError: (_err, storyId, context) => {
      // Rollback
      if (context?.previousDetail) {
        queryClient.setQueryData<Story>(storyKeys.detail(storyId), context.previousDetail);
      }
      for (const { queryKey, data } of context?.listCacheEntries ?? []) {
        queryClient.setQueryData<GetStoriesResult>(queryKey, data);
      }

      toast({
        variant: 'destructive',
        title: t('errorTitle'),
        description: t('toggleError'),
      });
    },

    onSuccess: (result, storyId) => {
      if (!result.success) {
        // Server returned an error payload — rollback optimistic update by
        // re-fetching, and surface a toast. Do NOT throw: in react-query v5
        // that would still not trigger onError, and earlier versions had
        // ambiguous behavior. Handle it inline (matches audio-stories hook).
        queryClient.invalidateQueries({ queryKey: storyKeys.lists() });
        queryClient.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
        toast({
          variant: 'destructive',
          title: t('errorTitle'),
          description: result.error ?? t('toggleError'),
        });
        return;
      }

      // Sync confirmed server values into caches
      const { isFavorite, favoritedAt } = result.data;

      queryClient.setQueryData<Story>(storyKeys.detail(storyId), (prev) =>
        prev ? { ...prev, isFavorite, favoritedAt } : prev
      );

      queryClient.getQueriesData<GetStoriesResult>({ queryKey: storyKeys.lists() }).forEach(
        ([queryKey, data]) => {
          if (!data) return;
          queryClient.setQueryData<GetStoriesResult>(queryKey, {
            ...data,
            stories: data.stories.map((s) =>
              s.id === storyId ? { ...s, isFavorite, favoritedAt } : s
            ),
          });
        }
      );
    },

    onSettled: (_data, _error, storyId) => {
      // Refetch to reconcile onlyFavorites-filtered lists
      queryClient.invalidateQueries({ queryKey: storyKeys.lists() });
      queryClient.invalidateQueries({ queryKey: storyKeys.detail(storyId) });
    },
  });
}
